import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { transformUploadsToResponse } from '../../../shared/models/UploadsResponse';
import type { UploadsResponse } from '../../../shared/models/UploadsResponse';
import { ProcessRepository } from '../../../data/repositories/ProcessRepository';

interface Step1Props {
  data: {
    nomeProcesso: string;
    documentoIdentificacao: File | null;
    comprovanteResidencia: File | null;
    comprovanteRenda: File | null;
  };
  onChange: (data: any) => void;
  onPrevious?: () => void;
  onUploadsComplete: (uploads: UploadsResponse) => void;
}

export default function Step1Documents({ data, onChange, onPrevious, onUploadsComplete }: Step1Props) {
  const navigate = useNavigate();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<string>('');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const processRepository = new ProcessRepository();

  const uploadAllDocuments = async (): Promise<any> => {
    try {
      const result = await processRepository.uploadDocuments(
        data.documentoIdentificacao!,
        data.comprovanteResidencia!,
        data.comprovanteRenda!,
        data.nomeProcesso
      );

      setUploadStatus('success');
      return result;
    } catch (error) {
      console.error('Erro ao fazer upload dos documentos:', error);
      setUploadStatus('error');
      throw error;
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    onChange({ [name]: value });
    
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, fieldName: string) => {
    const file = e.target.files?.[0] || null;
    
    if (file) {
      // Validar tipo de arquivo
      const allowedTypes = [
        'application/pdf',
        'image/jpeg',
        'image/jpg', 
        'image/png',
        'image/gif',
        'image/webp'
      ];
      
      if (!allowedTypes.includes(file.type)) {
        setErrors(prev => ({ ...prev, [fieldName]: 'Apenas arquivos PDF, JPG, PNG, GIF e WebP são permitidos' }));
        return;
      }
      
      // Validar tamanho do arquivo (5MB = 5 * 1024 * 1024 bytes)
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        setErrors(prev => ({ ...prev, [fieldName]: 'O arquivo deve ter no máximo 5MB' }));
        return;
      }
    }
    
    onChange({ [fieldName]: file });
    
    // Clear error when user selects a valid file
    if (errors[fieldName] && file) {
      setErrors(prev => ({ ...prev, [fieldName]: '' }));
    }

    // Clear upload status when file changes
    if (uploadStatus) {
      setUploadStatus('');
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!data.nomeProcesso.trim()) {
      newErrors.nomeProcesso = 'Nome do processo é obrigatório';
    }

    if (!data.documentoIdentificacao) {
      newErrors.documentoIdentificacao = 'Documento de identificação é obrigatório';
    }

    if (!data.comprovanteResidencia) {
      newErrors.comprovanteResidencia = 'Comprovante de residência é obrigatório';
    }

    if (!data.comprovanteRenda) {
      newErrors.comprovanteRenda = 'Comprovante de renda é obrigatório';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    // Upload all documents at once
    setUploading(true);

    try {
      const result = await uploadAllDocuments();

      setUploading(false);

      // Check if upload was successful
      if (result) {
        // Transform result to the required format
        const transformedUploads = transformUploadsToResponse(result, data);
        // Pass the transformed upload results to the parent component
        onUploadsComplete(transformedUploads);
        
        // Show success message and navigate to tracking page
        setShowSuccessMessage(true);
        setTimeout(() => {
          navigate('/habita/pastas');
        }, 3000);
      } else {
        // Show error message
        console.error('Erro ao fazer upload dos documentos. Tente novamente.');
      }
    } catch (error) {
      setUploading(false);
      console.error('Erro ao fazer upload dos documentos. Tente novamente.');
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getUploadStatusIcon = () => {
    switch (uploadStatus) {
      case 'success':
        return 'ri-check-line text-green-600';
      case 'error':
        return 'ri-close-line text-red-600';
      default:
        return '';
    }
  };

  const getUploadStatusText = () => {
    switch (uploadStatus) {
      case 'success':
        return 'Upload realizado com sucesso';
      case 'error':
        return 'Erro no upload';
      default:
        return '';
    }
  };

  const FileUpload = ({ 
    label, 
    fieldName, 
    file, 
    error, 
    description,
    icon 
  }: {
    label: string;
    fieldName: string;
    file: File | null;
    error?: string;
    description: string;
    icon: string;
  }) => (
    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-primary/50 transition-colors">
      <div className="text-center">
        <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <i className={`${icon} text-2xl text-primary`}></i>
        </div>
        <h3 className="font-instrument font-medium text-textPrimary mb-2">{label}</h3>
        <p className="text-sm font-instrument text-textSecondary mb-4">{description}</p>
        
        {file ? (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
            <div className="flex items-center justify-center space-x-2">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-file-line text-green-600"></i>
              </div>
              <span className="text-sm font-instrument text-green-700 font-medium">{file.name}</span>
              <span className="text-xs font-instrument text-green-600">({formatFileSize(file.size)})</span>
            </div>
            
            {/* Upload Status */}
            {uploadStatus && (
              <div className="mt-2 flex items-center justify-center space-x-2">
                <i className={`${getUploadStatusIcon()}`}></i>
                <span className={`text-xs font-instrument ${
                  uploadStatus === 'success' ? 'text-green-600' : 'text-red-600'
                }`}>
                  {getUploadStatusText()}
                </span>
              </div>
            )}
          </div>
        ) : null}
        
        <label className="cursor-pointer">
          <input
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={(e) => handleFileChange(e, fieldName)}
            className="hidden"
            disabled={uploading}
          />
          <span className={`px-6 py-2 rounded-lg font-instrument font-medium transition-colors cursor-pointer whitespace-nowrap inline-block ${
            uploading 
              ? 'bg-gray-400 text-gray-600 cursor-not-allowed' 
              : 'bg-primary hover:bg-primary/90 text-primary-foreground'
          }`}>
            {uploading ? 'Fazendo Upload...' : (file ? 'Trocar Arquivo' : 'Selecionar Arquivo')}
          </span>
        </label>
        
        <p className="text-xs font-instrument text-textSecondary mt-2">
          PDF, JPG, PNG até 5MB
        </p>
        
        {error && (
          <p className="mt-2 text-sm text-red-600 font-instrument">{error}</p>
        )}
      </div>
    </div>
  );

  return (
    <div>
      {/* Success Message Overlay */}
      {showSuccessMessage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-check-double-line text-3xl text-green-600"></i>
            </div>
            <h3 className="text-2xl font-fraunces font-bold text-textPrimary mb-4">
              Processo Enviado com Sucesso!
            </h3>
            <p className="font-instrument text-textSecondary mb-6">
              Seus documentos foram enviados e estão sendo analisados pela nossa equipe. 
              Você receberá atualizações sobre o andamento em breve.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="font-instrument text-blue-800 text-sm">
                <strong>Próximos passos:</strong> Acompanhe o status do seu processo em Pastas.
                Nossa equipe entrará em contato se precisar de documentos adicionais.
              </p>
            </div>
            <div className="flex items-center justify-center space-x-2 text-textSecondary">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-time-line"></i>
              </div>
              <span className="font-instrument text-sm">Redirecionando para Pastas...</span>
            </div>
          </div>
        </div>
      )}

      <div className="mb-8">
        <h2 className="text-2xl font-fraunces font-semibold text-textPrimary mb-2">
          Documentos
        </h2>
        <p className="font-instrument text-textSecondary">
          Envie os documentos necessários para análise do seu processo
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Nome do Processo */}
        <div>
          <label htmlFor="nomeProcesso" className="block text-sm font-instrument font-medium text-textPrimary mb-2">
            Nome do Processo *
          </label>
          <input
            type="text"
            id="nomeProcesso"
            name="nomeProcesso"
            value={data.nomeProcesso}
            onChange={handleInputChange}
            className={`block w-full px-3 py-3 border rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent font-instrument text-sm ${
              errors.nomeProcesso ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-primary'
            }`}
            placeholder="Ex: Processo Habitacional - João Silva"
          />
          {errors.nomeProcesso && (
            <p className="mt-1 text-sm text-red-600 font-instrument">{errors.nomeProcesso}</p>
          )}
        </div>

        <FileUpload
          label="Documento de Identificação"
          fieldName="documentoIdentificacao"
          file={data.documentoIdentificacao}
          error={errors.documentoIdentificacao}
          description="RG, CNH ou outro documento oficial com foto"
          icon="ri-id-card-line"
        />

        <FileUpload
          label="Comprovante de Residência"
          fieldName="comprovanteResidencia"
          file={data.comprovanteResidencia}
          error={errors.comprovanteResidencia}
          description="Conta de luz, água, telefone ou contrato de aluguel (últimos 3 meses)"
          icon="ri-home-line"
        />

        <FileUpload
          label="Comprovante de Renda"
          fieldName="comprovanteRenda"
          file={data.comprovanteRenda}
          error={errors.comprovanteRenda}
          description="Holerite, declaração de imposto de renda ou extrato bancário"
          icon="ri-money-dollar-circle-line"
        />

        <div className="flex flex-col sm:flex-row justify-between gap-4 pt-6">
          {onPrevious && (
            <button
              type="button"
              onClick={onPrevious}
              className="px-6 py-3 border border-gray-300 rounded-lg font-instrument font-medium text-textPrimary hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 inline-flex items-center justify-center mr-2">
                <i className="ri-arrow-left-line"></i>
              </div>
              Voltar
            </button>
          )}
          
          <button
            type="submit"
            disabled={uploading}
            className={`px-8 py-3 rounded-lg font-instrument font-semibold transition-colors cursor-pointer whitespace-nowrap ${
              uploading 
                ? 'bg-gray-400 text-gray-600 cursor-not-allowed' 
                : 'bg-primary hover:bg-primary/90 text-primary-foreground'
            }`}
          >
            {uploading ? 'Enviando Documentos...' : 'Finalizar Processo'}
            <div className="w-4 h-4 inline-flex items-center justify-center ml-2">
              {uploading ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>}
            </div>
          </button>
        </div>
      </form>
    </div>
  );
}
